import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatTelBR } from "@/lib/phoneUtils";

/**
 * ClienteAutocomplete — Input controlado com sugestões de clientes cadastrados.
 *
 * Ao digitar 2+ caracteres, dispara `GET /clientes/suggest?q=...` com debounce
 * de 300ms. Mostra até 10 resultados agrupados por (nome+telefone). Ao clicar
 * numa sugestão, chama `onFill({ cliente_nome, cliente_telefone, bairro_id,
 * regiao_id, bairro_nome })` — o form pai decide quais campos preencher.
 *
 * Comportamento:
 *   - onChange do input reflete no state pai via `onChange(value)`
 *   - Não intercepta Enter — usuário pode simplesmente terminar de digitar
 *   - Dropdown fecha no blur (com delay pra permitir clique)
 *   - Sem sugestões → dropdown não aparece
 */
export function ClienteAutocomplete({
  value, onChange, onFill, placeholder = "Nome", testId, className = "",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const blurTimer = useRef(null);
  const debounceTimer = useRef(null);
  const lastQuery = useRef("");
  const abortRef = useRef(null);

  useEffect(() => {
    const q = String(value || "").trim();
    // Sem query mínima, esconde e não busca
    if (q.length < 2) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      setSuggestions([]);
      setOpen(false);
      return;
    }
    // Evita rebuscar o mesmo termo
    if (q === lastQuery.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      lastQuery.current = q;
      setLoading(true);
      // Cancela request anterior se ainda pendente
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const r = await api.get(`/clientes/suggest?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        const rows = Array.isArray(r.data) ? r.data : [];
        setSuggestions(rows);
        setOpen(rows.length > 0);
      } catch (e) {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    // IMPORTANTE: sem cleanup fn — não cancelamos o debounce em re-renders
    // do parent (ele só é cancelado quando `value` muda ou desmontar).
  }, [value]);

  const handleSelect = (s) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onFill?.(s);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => {
          // Delay pro click no item ainda funcionar
          blurTimer.current = setTimeout(() => setOpen(false), 180);
        }}
        placeholder={placeholder}
        data-testid={testId}
        className="h-9 text-sm"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[220px] max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Buscando…</div>
          )}
          {suggestions.map((s, idx) => (
            <button
              key={`${s.cliente_nome}-${s.cliente_telefone}-${idx}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s)}
              className="block w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-slate-100 last:border-b-0 focus:bg-orange-50 focus:outline-none"
              data-testid={`${testId}-suggestion-${idx}`}
            >
              <div className="text-sm font-semibold text-slate-800 truncate">
                {s.cliente_nome}
              </div>
              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                {s.cliente_telefone && (
                  <span className="mono">{formatTelBR(s.cliente_telefone)}</span>
                )}
                {s.bairro_nome && <span>· {s.bairro_nome}</span>}
                {s.count > 1 && (
                  <span className="ml-auto text-[10px] bg-slate-100 rounded-full px-1.5 py-0.5">
                    {s.count} pedidos
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Hook para lookup por número de pedido — chamar no onBlur do input.
 * Se achou match e os campos-alvo estão vazios, chama `onFill`.
 *
 * Uso:
 *   const lookupPedido = usePedidoLookup();
 *   <Input onBlur={() => lookupPedido(numero_pedido, (data) => {...})} />
 */
export function usePedidoLookup() {
  return async (numero_pedido, onFill) => {
    const num = String(numero_pedido || "").trim();
    if (!num) return null;
    try {
      const r = await api.get(`/entregas/lookup?numero_pedido=${encodeURIComponent(num)}`);
      if (r.data?.found) {
        onFill?.(r.data);
        return r.data;
      }
    } catch {
      // silencioso — autofill é opcional
    }
    return null;
  };
}
